// Point ONE device at a firmware file, by unique number.
//
// ── Why by unique number and not by picking a row ──────────────────────────
// The devices that need this most are not in any list. A board fresh off the
// bench has `MasterUserId 0`, `LocationID 0` and `RoomId 0`, so every
// location-scoped query misses it, and the Tuya bench is time-windowed — it
// returned zero rows when this was written. E072A14582D0, the ESP8685/C3 unit,
// is exactly that case. The number printed on the board is the only handle that
// always works.
//
// ── Why this exists at all ─────────────────────────────────────────────────
// `DeviceType.OTABinFileId` holds ONE value, so a product cannot say "some of
// these are ESP8266 and some are ESP32-C3". `DevicesInstalled.OTABinFileId`
// overrides it per device, and that override is the only thing in the system
// that can express a mixed-chip fleet. OTA_CLAUDE.md §12.6 asks for "a per-chip
// URL convention before a second architecture ships" — this is it, and it
// already worked; it just had no UI.
//
// ⚠️ THE READ-BACK IS THE FEATURE. After writing, this asks
// `GetDeviceOTAUrlLink` — the call the firmware itself makes — and shows the
// answer. Everything in this area fails by reporting success (a device stamps
// `CodeUpdated` before it flashes, and a rejected wrong-chip image still leaves
// the row saying "updated"), so a mapping that is merely *written* is not worth
// much. One extra request turns it into a mapping that is *proven*.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
  Typography, Alert, AlertTitle, MenuItem, Chip, CircularProgress, Divider,
  LinearProgress,
} from '@mui/material';
import MemoryIcon from '@mui/icons-material/Memory';
import { getOtaBinFiles, getDeviceDetailFresh } from '../../core/api/api';
import { mapDeviceToBinFile, servedOtaUrl } from '../../core/devices/otaBinFiles';
import { askVersion } from '../../core/devices/firmwareVersion';
import {
  startOtaAndWatch, otaOutcome, otaPayload, OTA_TIMEOUT_MS, OTA_ACK,
} from '../../core/devices/otaProgress';
import { useApp } from '../../core/state/AppContext';
import { FIT_SM } from './fitDialog';

export default function DeviceFirmwareDialog({ open, onClose, uniqueNumber: initialUno = '' }) {
  const [uno, setUno] = useState(initialUno);
  const [bins, setBins] = useState([]);
  const [device, setDevice] = useState(null);
  const [servedNow, setServedNow] = useState('');
  const [pick, setPick] = useState('');
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const { userId } = useApp();
  const [version, setVersion] = useState(undefined);   // undefined = not asked
  const [asking, setAsking] = useState(false);

  // The running image, asked over MQTT. Nothing else in the system can answer
  // it — see core/devices/firmwareVersion.js for why each of the four
  // timestamp-shaped alternatives answers a different question.
  const checkVersion = useCallback(async () => {
    setAsking(true);
    try {
      setVersion(await askVersion(uno.trim(), userId));
    } finally {
      setAsking(false);
    }
  }, [uno, userId]);

  // ── Triggering the update, and watching it ────────────────────────────────
  // The Firmware screen cannot reach this device: it enumerates by location and
  // this board has none. So the trigger belongs here, next to the mapping it
  // depends on — map, verify the URL, update, verify the version, in one place.
  const [ota, setOta] = useState(null);          // null = not started
  const otaStop = useRef(null);
  const otaTimer = useRef(null);

  const stopOta = useCallback(() => {
    if (otaStop.current) { otaStop.current(); otaStop.current = null; }
    if (otaTimer.current) { clearTimeout(otaTimer.current); otaTimer.current = null; }
  }, []);

  useEffect(() => stopOta, [stopOta]);
  useEffect(() => { if (!open) stopOta(); }, [open, stopOta]);

  const sendUpdate = useCallback(() => {
    stopOta();
    setOta({ events: [], timedOut: false });
    otaStop.current = startOtaAndWatch(uno.trim(), userId, (ev) => {
      setOta((s) => (s ? { ...s, events: [...s.events, ev] } : s));
    });
    // §7: five minutes from `OTA started:` to the device coming back. Anything
    // tighter reports false failures on a slow link, and there is no
    // device-side overall timeout to lean on.
    otaTimer.current = setTimeout(() => {
      setOta((s) => (s ? { ...s, timedOut: true } : s));
      stopOta();
    }, OTA_TIMEOUT_MS);
  }, [uno, userId, stopOta]);

  const outcome = ota ? otaOutcome(ota.events) : null;

  useEffect(() => {
    if (!open) return;
    setUno(initialUno); setDevice(null); setServedNow(''); setPick('');
    setError(''); setResult(null);
    getOtaBinFiles()
      // Row 17 has an empty BinFileName; as an option it would read as blank
      // and un-point the device.
      .then((res) => setBins((Array.isArray(res.data) ? res.data : [])
        .filter((r) => String(r.BinFileName || '').trim())))
      .catch(() => setBins([]));
  }, [open, initialUno]);

  const look = useCallback(async () => {
    const n = uno.trim();
    if (!n) return;
    setLooking(true); setError(''); setDevice(null); setServedNow(''); setResult(null);
    try {
      const res = await getDeviceDetailFresh(n);
      const row = Array.isArray(res.data) ? res.data[0] : null;
      if (!row) { setError(`No device with unique number ${n}.`); return; }
      setDevice(row);
      setPick(row.OTABinFileId != null ? String(row.OTABinFileId) : '');
      // What it would download right now — before anything is changed, so the
      // operator can see whether there is even a problem to fix.
      setServedNow(await servedOtaUrl(n).catch(() => ''));
    } catch (e) {
      setError('Could not read that device — check the connection and the number.');
    } finally {
      setLooking(false);
    }
  }, [uno]);

  const save = useCallback(async () => {
    setSaving(true); setError(''); setResult(null);
    try {
      const row = bins.find((b) => String(b.OTABinFileId) === String(pick));
      const out = await mapDeviceToBinFile(uno.trim(), pick, row && row.BinFileName);
      setResult({ ...out, name: row && row.BinFileName });
    } catch (e) {
      setError('The mapping could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [bins, pick, uno]);

  const changed = device && String(pick) !== String(device.OTABinFileId ?? '');

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} {...FIT_SM}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25, fontSize: 16, fontWeight: 700 }}>
        <MemoryIcon fontSize="small" />
        Set a device&apos;s firmware file
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label="Unique number" size="small" sx={{ flex: 1 }}
            value={uno}
            onChange={(e) => setUno(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') look(); }}
            placeholder="E072A14582D0"
            disabled={saving}
            // The new generation derives it from the MAC, so it is 12 hex
            // characters where the older fleet is numeric. Anything that
            // assumed digits would miss exactly the boards this is for.
            helperText="As printed on the board — 12 hex characters on ESP32-C3 units, digits on older ones."
          />
          <Button
            variant="outlined" onClick={look} disabled={!uno.trim() || looking || saving}
            sx={{ mt: 0.25, textTransform: 'none' }}
          >
            {looking ? <CircularProgress size={18} /> : 'Find'}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}

        {device && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {device.DeviceName || device.DeviceTypeName || 'Device'}
              </Typography>
              <Chip size="small" label={`DeviceID ${device.DeviceID}`} sx={{ height: 20, fontSize: 11 }} />
              <Chip size="small" variant="outlined" label={device.DeviceTypeName || '—'} sx={{ height: 20, fontSize: 11 }} />
              {/* An unowned, unplaced board is the normal case here, not an
                  error — say so, or it reads as a broken record. */}
              {Number(device.LocationID) === 0 && (
                <Chip size="small" color="info" variant="outlined" label="not yet placed" sx={{ height: 20, fontSize: 11 }} />
              )}
            </Box>

            <Alert severity="info" sx={{ mb: 2, py: 0, '& .MuiAlert-message': { py: 0.75 } }}>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Downloads right now:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                {servedNow || '—'}
              </Typography>
            </Alert>

            {/* ⚠️ The ONE thing that reveals a silent rollback. Everything else
                — CodeUpdated, the build date, the retained startup packet —
                reports what was ATTEMPTED, so a reverted image reads as a
                successful update. Only the running image can say otherwise. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button
                size="small" variant="outlined" onClick={checkVersion} disabled={asking || saving}
                sx={{ textTransform: 'none' }}
              >
                {asking ? <CircularProgress size={16} /> : 'Ask what it is running'}
              </Button>
              {version === undefined && (
                <Typography variant="caption" color="text.secondary">
                  Sends VERSION over MQTT — the only thing that reports the running image.
                </Typography>
              )}
              {version === null && (
                <Typography variant="caption" color="text.secondary">
                  No answer. The device may be offline, or be an older ESP8266 build,
                  which has no VERSION command — this is not evidence of a fault.
                </Typography>
              )}
              {version && (
                <>
                  <Chip size="small" color="success" label={`FW ${version.version}`} sx={{ height: 20, fontSize: 11 }} />
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {`sha=${version.sha} · idf=${version.idf} · built ${version.built}`}
                  </Typography>
                </>
              )}
            </Box>

            <TextField
              select fullWidth size="small" label="Firmware file for this device"
              value={pick} onChange={(e) => setPick(e.target.value)} disabled={saving}
              helperText="Written to DevicesInstalled.OTABinFileId, which overrides the device type's."
            >
              {bins.map((b) => (
                <MenuItem key={b.OTABinFileId} value={String(b.OTABinFileId)}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
                    {b.BinFileName}
                  </Typography>
                </MenuItem>
              ))}
            </TextField>
          </>
        )}

        {/* Only offered once the served URL has been CONFIRMED — sending Update
            Code before that is how a device downloads the file you just
            stopped pointing it at. */}
        {result && result.ok && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined" color="warning" size="small"
              onClick={sendUpdate} disabled={!!ota && outcome === 'running' && !ota.timedOut}
              sx={{ textTransform: 'none' }}
            >
              {ota ? 'Send Update Code again' : 'Send Update Code now'}
            </Button>

            {ota && (
              <Box sx={{ mt: 1 }}>
                {outcome === 'running' && !ota.timedOut && <LinearProgress />}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {`Published ${otaPayload(uno.trim(), userId)}`}
                </Typography>
                {/* Every line verbatim, newest last, with the ack greyed —
                    it is published even when the update did not start. */}
                {ota.events.map((e, i) => (
                  <Typography
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: 12,
                      opacity: e.kind === OTA_ACK ? 0.5 : 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {e.text}
                  </Typography>
                ))}
                {ota.events.length === 0 && !ota.timedOut && (
                  <Typography variant="caption" color="text.secondary">
                    Waiting for the device to report…
                  </Typography>
                )}

                {outcome === 'failed' && (
                  <Alert severity="error" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.75 } }}>
                    <Typography variant="body2">
                      The device refused or could not complete the update. A repeated
                      {' '}
                      <code>OTA FAILED (download)</code>
                      {' usually means the image is for a different chip.'}
                    </Typography>
                  </Alert>
                )}
                {(outcome === 'flashed' || outcome === 'back') && (
                  // ⚠️ `OTA OK` is not proof the release changed: a crash-looping
                  // image is reverted 60 s after boot, having already published it.
                  <Alert severity="warning" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.75 } }}>
                    <Typography variant="body2">
                      It flashed and rebooted — but that is not proof the release changed.
                      An image that crash-loops is reverted about a minute after boot,
                      having already reported success. Wait for it to come back, then
                      press <b>Ask what it is running</b>.
                    </Typography>
                  </Alert>
                )}
                {ota.timedOut && outcome === 'running' && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.75 } }}>
                    <Typography variant="body2">
                      No conclusion after five minutes. Stopped listening — the device
                      may still be downloading; ask for its version in a while.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}

        {result && (
          <Alert severity={result.ok ? 'success' : 'warning'} sx={{ mt: 2 }}>
            <AlertTitle>{result.ok ? 'Mapped and confirmed' : 'Saved, but the server still serves something else'}</AlertTitle>
            <Typography variant="caption" sx={{ display: 'block' }}>
              GetDeviceOTAUrlLink now returns:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
              {result.url || '(nothing)'}
            </Typography>
            {result.ok ? (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                That is the call the firmware makes, so this is what the device will
                download on its next Update Code. It does not fetch it on its own.
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                {`Expected a URL ending /${result.name}. The write went through but the`}
                {' server resolves this device to a different file — do not send Update'}
                {' Code until that is explained.'}
              </Typography>
            )}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ py: 0.75 }}>
        <Button onClick={onClose} disabled={saving}>{result ? 'Close' : 'Cancel'}</Button>
        <Button
          variant="contained" onClick={save}
          disabled={!device || !pick || !changed || saving}
        >
          {saving ? <CircularProgress size={20} /> : 'Set and verify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
